import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login } = useContext(AuthContext);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const from = location.state?.from?.pathname || null;
  const inviteToken = searchParams.get('invite');
  const inviteInfo = searchParams.get('info');

  let inviteData = null;
  if (inviteInfo) {
    try { inviteData = JSON.parse(decodeURIComponent(inviteInfo)); } catch { inviteData = null; }
  }

  // Also check localStorage for pending invite token
  const effectiveInviteToken = inviteToken || localStorage.getItem('pendingInviteToken');

  useEffect(() => {
    if (inviteToken) {
      localStorage.setItem('pendingInviteToken', inviteToken);
    }
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('Please fill in all fields'); return; }

    setLoading(true);
    const result = await login(form.email, form.password);
    setLoading(false);

    if (result.success) {
      if (result.role === 'admin') { navigate('/admin', { replace: true }); return; }

      // Check for pending invitation — redirect to invite page
      const pendingInvite = effectiveInviteToken || localStorage.getItem('pendingInviteToken');
      if (pendingInvite) {
        localStorage.removeItem('pendingInviteToken');
        navigate(`/invite/${pendingInvite}`, { replace: true });
        return;
      }

      if (from) navigate(from, { replace: true });
      else navigate('/home', { replace: true });
    } else {
      setError(result.error || 'Invalid email or password');
    }
  };

  return (
    <div className="mobile-container bg-white flex flex-col">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col px-6 pt-12 pb-8">
        <button onClick={() => navigate('/')} className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mb-8">
          <SafeIcon name="ArrowLeft" className="text-gray-600" />
        </button>
        <div className="mb-8">
          <img src="https://cloud.sharemyimage.com/2026/08/16/App-Icon.png" alt="DayClap" className="w-36 h-36 rounded-3xl mb-5 shadow-sm" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
          <p className="text-gray-500">Sign in to continue creating special moments.</p>
        </div>
        {effectiveInviteToken && inviteData && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-pink-50 border border-pink-200 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon name="Heart" className="text-pink-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-pink-800">{inviteData.sender} sent you an invitation!</p>
                <p className="text-xs text-pink-600 mt-0.5">Sign in to respond</p>
              </div>
            </div>
          </motion.div>
        )}
        {effectiveInviteToken && !inviteData && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-pink-50 border border-pink-200 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon name="Mail" className="text-pink-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-pink-800">You have a pending invitation!</p>
                <p className="text-xs text-pink-600 mt-0.5">Sign in to view and respond</p>
              </div>
            </div>
          </motion.div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
            <div className="relative">
              <SafeIcon name="Mail" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <div className="relative">
              <SafeIcon name="Lock" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Enter your password" className="w-full pl-12 pr-12 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <SafeIcon name={showPassword ? 'EyeOff' : 'Eye'} />
              </button>
            </div>
          </div>
          {error && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-50 border border-red-100 text-red-600 text-sm p-3 rounded-xl flex items-center gap-2">
              <SafeIcon name="AlertCircle" />{error}
            </motion.div>
          )}
          <div className="text-right">
            <Link to="/forgot-password" className="text-sm text-pink-500 font-medium">Forgot password?</Link>
          </div>
          <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? (<><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing in...</>) : effectiveInviteToken ? 'Sign In & Respond ❤️' : 'Sign In'}
          </button>
        </form>
        <p className="text-center text-sm text-gray-500 mt-8">
          Don't have an account?{' '}
          <Link to={effectiveInviteToken ? `/signup?invite=${effectiveInviteToken}&info=${inviteInfo || ''}` : '/signup'} className="text-pink-500 font-semibold">Sign up</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Login;